import { match } from 'ts-pattern'

import type {
  AutomationAnalysis,
  IndeterminateReason,
  ResolutionContext,
  SimulationEvaluation,
  SimulationPath
} from '../../simulation.types'
import type {
  ExpressionEvaluation,
  ExpressionEvaluator
} from '../evaluateExpression'
import { matchPath } from './matchPath'

/**
 * What we've found so far. Once `matched` is set we've got our answer and stop
 * looking; the two flags remember why the earlier routes didn't work out, which
 * is what we fall back on when nothing matches at all.
 */
type PathSearch = {
  matched: SimulationPath | undefined
  hasUnsupportedPath: boolean
  hasFailedExpression: boolean
}

/**
 * Works through the routes in order and reports where the first one whose
 * conditions all hold ends up. That order is the order the rules were written
 * in, so the first match is the rule that wins.
 *
 * What each switch answered comes back alongside the outcome. It's the only
 * record of the run - the sandbox is closed the moment this returns - and it's
 * what a developer log has to show to explain the route we took.
 */
export const evaluatePaths = ({
  analysis,
  context,
  evaluate
}: {
  analysis: AutomationAnalysis
  context: ResolutionContext
  evaluate: ExpressionEvaluator
}): SimulationEvaluation => {
  const evaluations = new Map<string, ExpressionEvaluation>()

  const search = analysis.paths.reduce<PathSearch>(
    (acc, path) => {
      if (acc.matched) return acc

      // We couldn't fetch something this route reads, so we can't say whether
      // its conditions hold. Skip it and remember we did - if nothing else
      // matches, that's the honest reason why.
      if (path.unsupportedDependencies.length > 0) {
        return { ...acc, hasUnsupportedPath: true }
      }

      return match(matchPath({ path, context, evaluate, evaluations }))
        .with('MATCHED', () => ({ ...acc, matched: path }))
        .with('FAILED', () => ({ ...acc, hasFailedExpression: true }))
        .with('MISMATCHED', () => acc)
        .exhaustive()
    },
    {
      matched: undefined,
      hasUnsupportedPath: false,
      hasFailedExpression: false
    }
  )

  const expressionResults = Object.fromEntries(evaluations)

  const { matched } = search
  if (matched) {
    return {
      result: {
        outcome: 'RESOLVED',
        terminalType: matched.terminalType,
        terminalTask: matched.terminalTask,
        path: matched
      },
      expressionResults
    }
  }

  return {
    result: { outcome: 'INDETERMINATE', reason: indeterminateReason(search) },
    expressionResults
  }
}

/**
 * Nothing matched, so we say why, most useful reason first. Data we couldn't
 * fetch explains itself and is worth reporting over a condition that blew up;
 * both beat "nothing matched", which tells whoever's asking the least.
 */
const indeterminateReason = ({
  hasUnsupportedPath,
  hasFailedExpression
}: PathSearch): IndeterminateReason =>
  match({ hasUnsupportedPath, hasFailedExpression })
    .with({ hasUnsupportedPath: true }, () => 'UNSUPPORTED_DEPENDENCY' as const)
    .with({ hasFailedExpression: true }, () => 'EVALUATION_FAILED' as const)
    .otherwise(() => 'NO_MATCHING_PATH' as const)
